import taskManagerApi from "@/features/shared/services/taskManagerApi";
import { transformToRequestProject } from "@/features/shared/utils/helpers";
/**
 * Unified function to fetch projects.
 * This function can fetch:
 *  - Default projects (no pagination/filters),
 *  - All projects (if all=true), or
 *  - Projects in a specific page with filters.
 *
 * @param {number} [page] - The page number (if paginating).
 * @param {ProjectFilters} [filters] - Filtering criteria.
 * @param {boolean} [all=false] - If true, fetch all projects.
 * @returns {Promise<ProjectData>} - An object containing projects and meta information.
 */
export async function fetchProjects(page, filters, all = false, limit) {
    try {
        const params = {
            ...(page ? { page } : {}),
            ...(filters || {}),
            ...(all ? { all: true } : {}),
            ...(limit ? { limit } : {}),
        };
        const response = await taskManagerApi.get("/project", { params });
        return response.data.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la récupération des projets");
        }
    }
}
/**
 * Fetch a single project by ID.
 */
export async function getProjectById(id) {
    try {
        const response = await taskManagerApi.get(`/project/${id}`);
        return response.data.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error(`Erreur inconnue lors de la récupération du projet ID ${id}`);
        }
    }
}
/**
 * Create a new project.
 */
export async function createProject(project) {
    try {
        const response = await taskManagerApi.post("/project", transformToRequestProject(project));
        return response.data.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la création du projet");
        }
    }
}
/**
 * Update an existing project.
 */
export async function updateProject(id, project) {
    try {
        const response = await taskManagerApi.put(`/project/${id}`, transformToRequestProject(project));
        return response.data.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error(`Erreur inconnue lors de la mise à jour du projet ID ${id}`);
        }
    }
}
/**
 * Delete a project by its ID.
 */
export async function deleteProject(id) {
    try {
        await taskManagerApi.delete(`/project/${id}`);
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error(`Erreur inconnue lors de la suppression du projet ID ${id}`);
        }
    }
}
